/**
 * OnboardingStep1 — Identita e contatti (PR4 Fase 5).
 *
 * Campi precompilati readonly: nome, cognome, email, telefono.
 * Campi richiesti: dataNascita, sesso, luogoNascita, cf, indirizzo, citta, cap, provincia.
 *
 * Pattern: il componente NON gestisce form state proprio, si aggancia al
 * react-hook-form del parent via `register` + `errors` per evitare contesti
 * duplicati. Le chiavi sono namespaced sotto "step1.*".
 */

import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import type { OnboardingFormValues } from './onboarding-form-types';
import { FieldRow, SelectField, TextField } from '@shared/components/ui/FormField';

interface Step1Props {
  register: UseFormRegister<OnboardingFormValues>;
  errors: FieldErrors<OnboardingFormValues>;
}

export function OnboardingStep1({ register, errors }: Step1Props) {
  const e1 = errors.step1;
  return (
    <fieldset className="flex flex-col gap-4">
      <legend className="sr-only">Identita e contatti</legend>

      <FieldRow
        id="step1-cognome"
        label="Cognome"
        required
        readOnly
        error={e1?.cognome?.message}
      >
        {(a) => (
          <TextField
            {...a}
            {...register('step1.cognome')}
            readOnly
            autoComplete="family-name"
          />
        )}
      </FieldRow>

      <FieldRow
        id="step1-nome"
        label="Nome"
        required
        readOnly
        error={e1?.nome?.message}
      >
        {(a) => (
          <TextField
            {...a}
            {...register('step1.nome')}
            readOnly
            autoComplete="given-name"
          />
        )}
      </FieldRow>

      <FieldRow
        id="step1-email"
        label="Email"
        required
        readOnly
        error={e1?.email?.message}
      >
        {(a) => (
          <TextField
            {...a}
            {...register('step1.email')}
            type="email"
            readOnly
            autoComplete="email"
          />
        )}
      </FieldRow>

      <FieldRow
        id="step1-telefono"
        label="Telefono"
        required
        readOnly
        error={e1?.telefono?.message}
      >
        {(a) => (
          <TextField
            {...a}
            {...register('step1.telefono')}
            type="tel"
            readOnly
            autoComplete="tel"
          />
        )}
      </FieldRow>

      <FieldRow
        id="step1-dataNascita"
        label="Data di nascita"
        required
        error={e1?.dataNascita?.message}
      >
        {(a) => (
          <TextField
            {...a}
            {...register('step1.dataNascita')}
            type="date"
            autoComplete="bday"
          />
        )}
      </FieldRow>

      <FieldRow
        id="step1-sesso"
        label="Sesso"
        required
        error={e1?.sesso?.message}
      >
        {(a) => (
          <SelectField {...a} {...register('step1.sesso')}>
            <option value="">Seleziona...</option>
            <option value="F">Femmina</option>
            <option value="M">Maschio</option>
            <option value="altro">Altro</option>
          </SelectField>
        )}
      </FieldRow>

      <FieldRow
        id="step1-luogoNascita"
        label="Luogo di nascita"
        required
        error={e1?.luogoNascita?.message}
      >
        {(a) => <TextField {...a} {...register('step1.luogoNascita')} />}
      </FieldRow>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow
          id="step1-provinciaNascita"
          label="Provincia nascita"
          hint="2 lettere (es. MB)"
          error={e1?.provinciaNascita?.message}
        >
          {(a) => (
            <TextField
              {...a}
              {...register('step1.provinciaNascita')}
              maxLength={2}
              style={{ textTransform: 'uppercase' }}
            />
          )}
        </FieldRow>

        <FieldRow
          id="step1-nazioneNascita"
          label="Nazione nascita"
          error={e1?.nazioneNascita?.message}
        >
          {(a) => (
            <TextField {...a} {...register('step1.nazioneNascita')} defaultValue="Italia" />
          )}
        </FieldRow>
      </div>

      <FieldRow
        id="step1-cf"
        label="Codice Fiscale"
        required
        hint="16 caratteri (es. RSSMRA80A01H501U)"
        error={e1?.cf?.message}
      >
        {(a) => (
          <TextField
            {...a}
            {...register('step1.cf')}
            maxLength={16}
            style={{ textTransform: 'uppercase' }}
            autoComplete="off"
          />
        )}
      </FieldRow>

      <FieldRow
        id="step1-indirizzo"
        label="Indirizzo residenza"
        required
        error={e1?.indirizzo?.message}
      >
        {(a) => (
          <TextField
            {...a}
            {...register('step1.indirizzo')}
            autoComplete="street-address"
          />
        )}
      </FieldRow>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FieldRow
          id="step1-citta"
          label="Comune"
          required
          hint="Comune di residenza"
          error={e1?.citta?.message}
        >
          {(a) => (
            <TextField {...a} {...register('step1.citta')} autoComplete="address-level2" />
          )}
        </FieldRow>

        <FieldRow
          id="step1-cap"
          label="CAP"
          required
          error={e1?.cap?.message}
        >
          {(a) => (
            <TextField
              {...a}
              {...register('step1.cap')}
              maxLength={5}
              inputMode="numeric"
              autoComplete="postal-code"
            />
          )}
        </FieldRow>

        <FieldRow
          id="step1-provincia"
          label="Provincia"
          required
          hint="2 lettere"
          error={e1?.provincia?.message}
        >
          {(a) => (
            <TextField
              {...a}
              {...register('step1.provincia')}
              maxLength={2}
              style={{ textTransform: 'uppercase' }}
              autoComplete="address-level1"
            />
          )}
        </FieldRow>
      </div>
    </fieldset>
  );
}
